import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import {
  CheckCircle2, Sparkles, Lock, TreePine, Loader2, BookOpen, Play,
  Trophy, Flame, Star, ChevronRight, Award,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/learner/tree")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Learning Tree — MAKAI" }] }),
  component: TreePage,
});

type Subject = { id: string; name: string; code: string | null };
type Lesson = {
  id: string; title: string; topic: string | null; subject_id: string;
  sort_order: number | null; duration_min: number | null; content_type: string;
};
type Completion = { lesson_id: string; status: string };
type Badge = { id: string; name: string; description: string | null; icon: string | null };

const XP_PER_LESSON = 10;

function TreePage() {
  const { ctx, loading } = useLearnerContext();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [comps, setComps] = useState<Record<string, Completion>>({});
  const [xp, setXp] = useState<{ points: number; current_streak: number } | null>(null);
  const [earnedBadges, setEarnedBadges] = useState<Badge[]>([]);
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [activeSubject, setActiveSubject] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!ctx) return;
    setFetching(true);
    (async () => {
      const subs: Subject[] = ctx.classId
        ? (((await supabase.from("class_subjects")
            .select("subject:subjects_catalog(id, name, code)")
            .eq("class_id", ctx.classId)).data ?? []) as { subject: Subject | null }[])
            .map((r) => r.subject!).filter(Boolean)
        : [];
      setSubjects(subs);
      setActiveSubject((prev) => prev ?? subs[0]?.id ?? null);

      const { data: ls } = await supabase.from("lessons")
        .select("id, title, topic, subject_id, sort_order, duration_min, content_type")
        .eq("school_id", ctx.schoolId).eq("published", true)
        .order("subject_id").order("topic").order("sort_order");
      setLessons((ls as Lesson[] | null) ?? []);

      const { data: cs } = await supabase.from("lesson_completions")
        .select("lesson_id, status").eq("learner_id", ctx.learnerId);
      const cmap: Record<string, Completion> = {};
      ((cs ?? []) as Completion[]).forEach((c) => { cmap[c.lesson_id] = c; });
      setComps(cmap);

      const { data: x } = await supabase.from("learner_xp")
        .select("points, current_streak").eq("learner_id", ctx.learnerId).maybeSingle();
      setXp(x as { points: number; current_streak: number } | null);

      const { data: ab } = await supabase.from("badges")
        .select("id, name, description, icon");
      setAllBadges((ab as Badge[] | null) ?? []);

      const { data: lb } = await supabase.from("learner_badges")
        .select("badge:badges(id, name, description, icon)")
        .eq("learner_id", ctx.learnerId);
      setEarnedBadges(((lb ?? []) as { badge: Badge | null }[]).map((r) => r.badge!).filter(Boolean));

      setFetching(false);
    })();
  }, [ctx]);

  // Build tree: subject → topic → ordered lessons; prerequisite = previous lesson in same topic
  const tree = useMemo(() => {
    const bySubject: Record<string, Record<string, Lesson[]>> = {};
    lessons.forEach((l) => {
      const t = l.topic ?? "General";
      (bySubject[l.subject_id] ??= {})[t] ??= [];
      bySubject[l.subject_id][t].push(l);
    });
    return bySubject;
  }, [lessons]);

  // Per-subject completion %
  const subjectStats = useMemo(() => {
    const m: Record<string, { done: number; total: number; pct: number }> = {};
    subjects.forEach((s) => {
      const ls = lessons.filter((l) => l.subject_id === s.id);
      const done = ls.filter((l) => comps[l.id]?.status === "completed").length;
      m[s.id] = { done, total: ls.length, pct: ls.length ? Math.round((done / ls.length) * 100) : 0 };
    });
    return m;
  }, [subjects, lessons, comps]);

  const overallPct = useMemo(() => {
    if (!lessons.length) return 0;
    const done = lessons.filter((l) => comps[l.id]?.status === "completed").length;
    return Math.round((done / lessons.length) * 100);
  }, [lessons, comps]);

  // AI recommendations: lessons that are unlocked, not yet completed, ranked by:
  // (1) in-progress first, (2) subject with lowest completion %, (3) earliest sort
  const recommendations = useMemo(() => {
    const candidates: { lesson: Lesson; subject: Subject; reason: string; score: number }[] = [];
    Object.entries(tree).forEach(([subjectId, topics]) => {
      const subject = subjects.find((s) => s.id === subjectId);
      if (!subject) return;
      Object.values(topics).forEach((topicLessons) => {
        for (let i = 0; i < topicLessons.length; i++) {
          const l = topicLessons[i];
          const c = comps[l.id];
          if (c?.status === "completed") continue;
          const prev = i > 0 ? topicLessons[i - 1] : null;
          const unlocked = !prev || comps[prev.id]?.status === "completed";
          if (!unlocked) break; // stop scanning this topic; rest are locked
          const inProgress = c?.status === "in_progress";
          const subjPct = subjectStats[subjectId]?.pct ?? 0;
          candidates.push({
            lesson: l, subject,
            reason: inProgress
              ? "Continue where you left off"
              : subjPct < 30 ? `Build momentum in ${subject.name}`
              : `Next step in ${l.topic ?? "your path"}`,
            score: (inProgress ? 1000 : 0) + (100 - subjPct) + (10 - Math.min(i, 10)),
          });
          break; // one rec per topic
        }
      });
    });
    return candidates.sort((a, b) => b.score - a.score).slice(0, 3);
  }, [tree, subjects, comps, subjectStats]);

  if (loading) return <AppShell role="learner"><Spinner /></AppShell>;

  const activeTopics = activeSubject ? tree[activeSubject] ?? {} : {};
  const activeSubjectObj = subjects.find((s) => s.id === activeSubject);

  return (
    <AppShell role="learner" title="Your Learning Tree" subtitle="Grow your knowledge — one lesson at a time.">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 text-foreground shadow-card">
        <div className="pattern-kente absolute inset-0 opacity-10" aria-hidden />
        <div className="relative flex flex-wrap items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary-soft text-primary">
            <TreePine className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-xl font-bold">Mighty Baobab in progress</h2>
            <p className="text-sm text-muted-foreground">
              {overallPct}% of your curriculum complete · {lessons.filter((l) => comps[l.id]?.status === "completed").length} of {lessons.length} lessons
            </p>
          </div>
          <div className="flex gap-2">
            <Stat icon={<Star className="h-4 w-4" />} label="XP" value={xp?.points ?? 0} />
            <Stat icon={<Flame className="h-4 w-4" />} label="Streak" value={`${xp?.current_streak ?? 0}d`} />
            <Stat icon={<Trophy className="h-4 w-4" />} label="Badges" value={`${earnedBadges.length}/${allBadges.length}`} />
          </div>
        </div>
        <div className="relative mt-5 h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-gold transition-all" style={{ width: `${overallPct}%` }} />
        </div>
      </section>


      {fetching && <div className="mt-6"><Spinner /></div>}

      {/* AI recommendations */}
      {recommendations.length > 0 && (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-4 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-gold/20 text-gold-foreground">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <h3 className="font-display font-bold">Recommended for you</h3>
              <p className="text-xs text-muted-foreground">Smart picks based on your progress</p>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {recommendations.map((r) => (
              <Link key={r.lesson.id} to="/learner/lessons" search={{ subject: r.subject.id } as never}
                className="group flex flex-col rounded-xl border border-border bg-background p-4 transition hover:border-primary hover:shadow-soft">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary">
                  <Sparkles className="h-3 w-3" /> {r.subject.name}
                </div>
                <div className="mt-2 font-semibold leading-snug line-clamp-2">{r.lesson.title}</div>
                <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{r.reason}</div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1 rounded-md bg-primary-soft px-2 py-0.5 font-semibold text-primary">
                    +{XP_PER_LESSON} XP
                  </span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary">
                    Start <ChevronRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Subject roadmap selector */}
      <section className="mt-6">
        <div className="-mx-1 mb-4 flex gap-2 overflow-x-auto px-1 pb-1">
          {subjects.map((s) => {
            const st = subjectStats[s.id];
            const active = activeSubject === s.id;
            return (
              <button key={s.id} onClick={() => setActiveSubject(s.id)}
                className={cn(
                  "shrink-0 rounded-xl border px-4 py-2.5 text-left transition",
                  active ? "border-primary bg-primary text-primary-foreground shadow-soft" : "border-border bg-card hover:border-primary/50",
                )}>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <BookOpen className="h-3.5 w-3.5" /> {s.name}
                </div>
                <div className={cn("mt-0.5 text-[11px]", active ? "text-primary-foreground/85" : "text-muted-foreground")}>
                  {st?.done ?? 0}/{st?.total ?? 0} · {st?.pct ?? 0}%
                </div>
              </button>
            );
          })}
          {subjects.length === 0 && !fetching && (
            <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">
              No subjects assigned to your class yet.
            </div>
          )}
        </div>

        {/* Topic roadmap for active subject */}
        {activeSubjectObj && (
          <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h3 className="font-display text-lg font-bold">{activeSubjectObj.name} roadmap</h3>
                <p className="text-xs text-muted-foreground">
                  Complete lessons in order to unlock the next step.
                </p>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-bold text-primary">{subjectStats[activeSubjectObj.id]?.pct ?? 0}%</div>
                <div className="text-[11px] text-muted-foreground">complete</div>
              </div>
            </div>

            {Object.keys(activeTopics).length === 0 && (
              <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                No lessons published for this subject yet.
              </div>
            )}

            <div className="space-y-6">
              {Object.entries(activeTopics).map(([topic, topicLessons]) => {
                const done = topicLessons.filter((l) => comps[l.id]?.status === "completed").length;
                const pct = Math.round((done / topicLessons.length) * 100);
                return (
                  <div key={topic}>
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="font-display text-base font-bold">{topic}</h4>
                      <span className="text-[11px] font-semibold text-muted-foreground">{done}/{topicLessons.length} · {pct}%</span>
                    </div>
                    <ol className="space-y-2">
                      {topicLessons.map((l, i) => {
                        const c = comps[l.id];
                        const isDone = c?.status === "completed";
                        const isCurrent = c?.status === "in_progress";
                        const prev = i > 0 ? topicLessons[i - 1] : null;
                        const unlocked = !prev || comps[prev.id]?.status === "completed";
                        const status: "done" | "current" | "available" | "locked" =
                          isDone ? "done" : isCurrent ? "current" : unlocked ? "available" : "locked";
                        return (
                          <li key={l.id} className="relative flex items-stretch gap-3">
                            <div className="relative flex flex-col items-center">
                              <div className={cn(
                                "grid h-9 w-9 place-items-center rounded-full border-2 shrink-0",
                                status === "done" && "border-primary bg-primary text-primary-foreground",
                                status === "current" && "border-gold bg-gold text-gold-foreground animate-pulse",
                                status === "available" && "border-primary/40 bg-primary-soft text-primary",
                                status === "locked" && "border-border bg-muted text-muted-foreground",
                              )}>
                                {status === "done" ? <CheckCircle2 className="h-4 w-4" />
                                  : status === "current" ? <Sparkles className="h-4 w-4" />
                                  : status === "available" ? <Play className="h-4 w-4" />
                                  : <Lock className="h-4 w-4" />}
                              </div>
                              {i < topicLessons.length - 1 && (
                                <div className={cn("w-px flex-1 my-1", isDone ? "bg-primary" : "bg-border")} />
                              )}
                            </div>
                            <div className={cn(
                              "min-w-0 flex-1 rounded-xl border p-3 mb-1 transition",
                              status === "locked" ? "border-dashed border-border bg-muted/30 opacity-70" : "border-border bg-background hover:border-primary/50",
                            )}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="truncate font-semibold">{l.title}</div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                                    <span className="uppercase">{l.content_type}</span>
                                    <span>·</span>
                                    <span>{l.duration_min ?? 10} min</span>
                                    {prev && status !== "done" && (
                                      <>
                                        <span>·</span>
                                        <span className="italic">requires: {prev.title}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="shrink-0 text-right">
                                  <div className={cn("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold",
                                    isDone ? "bg-primary-soft text-primary" : "bg-muted text-muted-foreground",
                                  )}>
                                    <Star className="h-3 w-3" /> {XP_PER_LESSON} XP
                                  </div>
                                </div>
                              </div>
                              {status !== "locked" && (
                                <div className="mt-2 flex items-center justify-end">
                                  <Link to="/learner/lessons" search={{ subject: l.subject_id } as never}
                                    className={cn(
                                      "inline-flex items-center gap-1 rounded-lg px-3 py-1 text-xs font-semibold",
                                      isDone ? "bg-primary-soft text-primary" : "bg-primary text-primary-foreground",
                                    )}>
                                    {isDone ? "Review" : isCurrent ? "Resume" : "Start"} <ChevronRight className="h-3 w-3" />
                                  </Link>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Badges */}
      {allBadges.length > 0 && (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-4 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary-soft text-primary">
              <Award className="h-4 w-4" />
            </div>
            <div>
              <h3 className="font-display font-bold">Badges</h3>
              <p className="text-xs text-muted-foreground">{earnedBadges.length} of {allBadges.length} unlocked</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {allBadges.map((b) => {
              const earned = earnedBadges.some((e) => e.id === b.id);
              return (
                <div key={b.id} className={cn(
                  "flex items-center gap-3 rounded-xl border p-3 transition",
                  earned ? "border-gold/50 bg-gold/10" : "border-dashed border-border bg-muted/30 opacity-70",
                )}>
                  <div className={cn(
                    "grid h-10 w-10 place-items-center rounded-full text-lg shrink-0",
                    earned ? "bg-gold text-gold-foreground" : "bg-muted text-muted-foreground",
                  )}>
                    {earned ? (b.icon ?? "🏅") : <Lock className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{b.name}</div>
                    {b.description && <div className="truncate text-[11px] text-muted-foreground">{b.description}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </AppShell>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-center">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">{icon}{label}</div>
      <div className="mt-0.5 font-display text-base font-bold text-foreground">{value}</div>
    </div>

  );
}

function Spinner() {
  return <div className="grid place-items-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
}
